import c from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useToasts } from 'react-toast-notifications';
import Header from '../../../components/Header';
import { Pill } from '../../../components/Pill';
import SnippetBox from '../../../components/SnippetBox';
import { SnippetContainerBox } from '../../../components/SnippetContainer';
import Tabs from '../../../components/Tabs';
import { Feature, Plan, PricingPage } from '../../../types/pricingPage';
import { SNIPPET_SERVER_URL } from '../../../utils/constants';
import { useAxios } from '../../../utils/hooks';
import HtmlTagName from '../../../components/HtmlTagName';

const LIBRARY_SNIPPET = `<script src="${SNIPPET_SERVER_URL}/pricewell.js" async="async"></script>`;
const getSnippet = (page: PricingPage, type: 'email' | 'stripeId') =>
    `window.pricewell.features('${page.id}',{${
        type === 'email' ? "email: 'CUSTOMER_EMAIL_HERE'" : "stripeId: 'CUSTOMER_STRIPE_ID_HERE'"
    }})`;

export default function ConfigureFeatureFlags() {
    const { pageId } = useParams<{ pageId: string }>();

    const { addToast } = useToasts();
    const axios = useAxios();
    const [page, setPage] = useState<PricingPage>();
    useEffect(() => {
        if (!axios.current) {
            return;
        }

        axios.current
            .get<PricingPage>(`/pricing-pages/${pageId}`)
            .then((res) => setPage(res.data))
            .catch((err) =>
                addToast('Something went wrong. Please reload the page and try again.', { appearance: 'error' })
            );
    }, [axios, addToast, pageId]);

    const snippetByEmail = useMemo(() => (page ? getSnippet(page, 'email') : ''), [page]);
    const snippetByStripeId = useMemo(() => (page ? getSnippet(page, 'stripeId') : ''), [page]);
    const allFeatures = useMemo(() => (page ? page.plans.flatMap((plan) => plan.features) : []), [page]);

    return (
        <div className="flex flex-col mx-auto">
            <div
                className={c('grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8', {
                    '2xl:grid-cols-3': (page?.plans.length ?? 0) > 2,
                })}
            >
                {page?.plans.map((plan) => (
                    <PlanTable plan={plan} allFeatures={allFeatures} />
                ))}
            </div>
            <div className="w-2/3 mx-auto rounded">
                <SnippetContainerBox>
                    <Header>Code Snippet</Header>
                    <div className="px-8">
                        <div className="py-4">
                            <p className="pb-2">
                                <strong>First: </strong>copy the following into the <HtmlTagName name="head" /> tag of
                                your HTML.
                            </p>

                            <SnippetBox snippet={LIBRARY_SNIPPET} />
                        </div>
                        <div className="py-4">
                            <p className="pb-2">
                                <strong>Then:</strong> use the following JavaScript function to get the enabled features
                                for a customer.
                            </p>

                            <div className="flex-1">
                                <Tabs
                                    tabs={[
                                        {
                                            title: 'Email (default)',
                                            id: 'email',
                                            body: (
                                                <div>
                                                    <SnippetBox snippet={snippetByEmail} />
                                                </div>
                                            ),
                                        },
                                        {
                                            title: 'Stripe Id',
                                            id: 'stripeId',
                                            body: (
                                                <div>
                                                    <SnippetBox snippet={snippetByStripeId} />
                                                    <p className="mt-8 flex">
                                                        <Pill title="Tip" />
                                                        <span className="ml-2">
                                                            You need to replace{' '}
                                                            <pre className="inline">CUSTOMER_STRIPE_ID_HERE</pre> with
                                                            the Stripe Id of your customer.
                                                        </span>
                                                    </p>
                                                </div>
                                            ),
                                        },
                                    ]}
                                />
                                <strong>Response (Example):</strong>
                                <pre className="bg-gray-300 p-4 rounded-lg shadow-md mt-2">
                                    {`
{
    "plan": "Startup Plan",
    "features": [
        {
        "id": "74635f5d-6260-4ae6-9aea-da8fff3237ea",
        "name": "Example Feature",
        "slug": "example-feature"
        },
    ]
}
                                    `}
                                </pre>
                            </div>
                        </div>
                    </div>
                </SnippetContainerBox>
            </div>
        </div>
    );
}

function PlanTable(props: { plan: Plan; allFeatures: Feature[] }) {
    const features = props.allFeatures.filter(
        (feature) => feature.name && (feature.allPlans || props.plan.features.map((f) => f.id).includes(feature.id))
    );
    return (
        <div className="flex-1 w-full mx-auto">
            <Header>{props.plan.name}</Header>
            <div className="bg-white shadow-md rounded my-6">
                <table className="min-w-max w-full table-auto">
                    <thead>
                        <tr className="bg-gray-200 text-gray-600 uppercase text-sm leading-normal">
                            <th className="py-3 px-6 text-left">Feature</th>
                            <th className="py-3 px-6 text-left">Slug</th>
                        </tr>
                    </thead>
                    <tbody className="text-gray-600 text-sm font-light">
                        {features.map((feature) => (
                            <tr key={feature.id} className="border-b border-gray-200 hover:bg-gray-100">
                                <td className="py-3 px-6 text-left whitespace-nowrap">
                                    <div className="flex items-center">
                                        <span className="font-medium text-base">{feature.name}</span>
                                    </div>
                                </td>
                                <td className="py-3 px-6 text-left">
                                    <div className="flex items-center">
                                        <SnippetBox snippet={feature.slug} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
